import { useMemo } from "react";
import {
  MapPin,
  Truck,
  Cpu,
  Radar,
  ShieldAlert,
  Wifi,
  CheckCircle2,
  CircleSlash,
} from "lucide-react";
import { useSites, useAssets, useDevices, useDetectionZones } from "../../lib/queries";
import {
  GlassCard,
  StatCard,
  SectionTag,
  Pill,
  Spinner,
  EmptyState,
} from "../../components/ui";
import { fmtNum, timeAgo, EMESRT_COLOR } from "../../lib/format";
import type { Site, AssetRow, Device, DetectionZone } from "../../types/api";

export default function Configure() {
  const sites = useSites();
  const assets = useAssets();
  const devices = useDevices();
  const zones = useDetectionZones();

  const loading =
    sites.isLoading || assets.isLoading || devices.isLoading || zones.isLoading;

  const siteList: Site[] = sites.data ?? [];
  const assetList: AssetRow[] = assets.data ?? [];
  const deviceList: Device[] = devices.data ?? [];
  const zoneList: DetectionZone[] = zones.data ?? [];

  const devicesOnline = useMemo(
    () => deviceList.filter((d) => d.online).length,
    [deviceList]
  );

  const taxonomy = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of assetList) {
      const label = a.taxonomy_label ?? "Unclassified";
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [assetList]);

  if (loading) {
    return (
      <GlassCard>
        <Spinner label="Loading site hierarchy & fleet…" />
      </GlassCard>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <SectionTag>CONFIGURE · ONBOARDING &amp; POLICY</SectionTag>
        <h1 className="text-xl font-semibold">Site Hierarchy &amp; Device Fleet</h1>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="SITES" value={fmtNum(siteList.length)} accent="var(--aurora-cyan)" />
        <StatCard label="ASSETS" value={fmtNum(assetList.length)} accent="var(--aurora-teal)" />
        <StatCard
          label="DEVICES ONLINE"
          value={`${fmtNum(devicesOnline)} / ${fmtNum(deviceList.length)}`}
          accent="var(--pacific)"
        />
        <StatCard
          label="DETECTION ZONES"
          value={fmtNum(zoneList.length)}
          accent="var(--accent)"
        />
      </div>

      {/* Sites + Asset taxonomy */}
      <div className="grid gap-3 lg:grid-cols-2">
        {/* Sites */}
        <GlassCard>
          <div className="mb-3 flex items-center gap-2">
            <MapPin size={15} className="text-aurora-cyan" />
            <SectionTag>SITE HIERARCHY</SectionTag>
          </div>
          {siteList.length === 0 ? (
            <EmptyState>No sites onboarded yet.</EmptyState>
          ) : (
            <div className="max-h-[320px] space-y-1.5 overflow-y-auto pr-1">
              {siteList.map((s) => {
                const hasGeo = s.latitude != null && s.longitude != null;
                return (
                  <div
                    key={s.id}
                    className="flex items-center gap-3 rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2 text-xs"
                  >
                    <span className="flex-1 truncate font-medium text-ink">{s.title}</span>
                    {hasGeo ? (
                      <span className="flex items-center gap-1 text-aurora-cyan" title="Geo-located">
                        <MapPin size={13} />
                      </span>
                    ) : (
                      <span className="text-slatey" title="No coordinates">
                        —
                      </span>
                    )}
                    <Pill color="#2DD4BF">{fmtNum(s.asset_count)} assets</Pill>
                  </div>
                );
              })}
            </div>
          )}
        </GlassCard>

        {/* Asset taxonomy */}
        <GlassCard>
          <div className="mb-3 flex items-center gap-2">
            <Truck size={15} className="text-aurora-teal" />
            <SectionTag>ASSET TAXONOMY</SectionTag>
          </div>
          <p className="mb-3 text-[11px] leading-relaxed text-slatey">
            OEM-agnostic abstract asset taxonomy — onboard without rip-and-replace.
          </p>
          {taxonomy.length === 0 ? (
            <EmptyState>No assets registered.</EmptyState>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap gap-2">
                {taxonomy.map(([label, count]) => (
                  <Pill key={label} color="#22D3EE">
                    {label} · {fmtNum(count)}
                  </Pill>
                ))}
              </div>
              <div className="max-h-[220px] space-y-1 overflow-y-auto pr-1">
                {assetList.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/[0.03] px-3 py-1.5 text-[11px]"
                  >
                    <span className="mono w-20 shrink-0 truncate text-ink">{a.registration}</span>
                    <span className="w-24 shrink-0 truncate text-slatey">{a.machine_type}</span>
                    <span className="flex-1 truncate text-aurora-cyan">
                      {a.taxonomy_label ?? "Unclassified"}
                    </span>
                    <span className="hidden w-24 shrink-0 truncate text-slatey md:block">
                      {a.site_title ?? "—"}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </GlassCard>
      </div>

      {/* Devices / edge agents — centerpiece */}
      <GlassCard>
        <div className="mb-1 flex items-center gap-2">
          <Cpu size={15} className="text-pacific" />
          <SectionTag>DEVICES · EDGE AGENTS</SectionTag>
        </div>
        <p className="mb-3 text-[11px] leading-relaxed text-slatey">
          Edge agent validation — each agent validated on the customer&apos;s SoC + carrier.
        </p>
        {deviceList.length === 0 ? (
          <EmptyState>No edge devices provisioned.</EmptyState>
        ) : (
          <div className="max-h-[460px] overflow-auto pr-1">
            <table className="w-full min-w-[720px] text-left text-[11px]">
              <thead className="sticky top-0 z-10 bg-[#0b1220]/90 backdrop-blur">
                <tr className="text-slatey">
                  <th className="px-2 py-2 font-medium">DEVICE</th>
                  <th className="px-2 py-2 font-medium">ASSET</th>
                  <th className="px-2 py-2 font-medium">STATUS</th>
                  <th className="px-2 py-2 font-medium">SOC</th>
                  <th className="px-2 py-2 font-medium">CARRIER</th>
                  <th className="px-2 py-2 font-medium">AGENT</th>
                  <th className="px-2 py-2 font-medium">CAPABILITIES</th>
                </tr>
              </thead>
              <tbody>
                {deviceList.map((d) => (
                  <tr key={d.id} className="border-t border-white/5 align-top">
                    <td className="px-2 py-2">
                      <span className="mono text-ink">#{d.ext_id}</span>
                    </td>
                    <td className="px-2 py-2 text-slatey">
                      {d.asset_registration ?? "—"}
                    </td>
                    <td className="px-2 py-2">
                      {d.online ? (
                        <span className="flex items-center gap-1 text-aurora-teal">
                          <CheckCircle2 size={13} /> online
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-slatey">
                          <CircleSlash size={13} /> offline
                        </span>
                      )}
                      {d.last_online && (
                        <div className="mt-0.5 text-[10px] text-slatey">
                          {timeAgo(d.last_online)}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-slatey">{d.soc}</td>
                    <td className="px-2 py-2">
                      <span className="flex items-center gap-1 text-slatey">
                        <Wifi size={12} /> {d.carrier}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <span className="mono text-slatey">{d.edge_agent_version}</span>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(d.capability_flags).map(([flag, on]) => (
                          <Pill key={flag} color={on ? "#22D3EE" : undefined}>
                            {flag.toUpperCase()}
                          </Pill>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* Detection zones / EMESRT policy */}
      <GlassCard>
        <div className="mb-3 flex items-center gap-2">
          <Radar size={15} className="text-accent" />
          <SectionTag>DETECTION ZONES · EMESRT POLICY</SectionTag>
        </div>
        {zoneList.length === 0 ? (
          <EmptyState>No detection zones configured.</EmptyState>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {zoneList.map((z) => (
              <ZoneCard key={z.id} zone={z} />
            ))}
          </div>
        )}
      </GlassCard>
    </div>
  );
}

function ZoneCard({ zone }: { zone: DetectionZone }) {
  const emColor = EMESRT_COLOR[zone.emesrt_level] ?? "#64748B";
  // Scale the inner red ellipse relative to the advisory ring; clamp so it stays visible.
  const ratio = zone.advisory_zone_m > 0 ? zone.red_zone_m / zone.advisory_zone_m : 0.25;
  const redRx = Math.max(14, Math.min(70, ratio * 70));
  const redRy = redRx * 0.4;

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium text-ink">{zone.name}</span>
        <Pill color={emColor}>{zone.emesrt_level}</Pill>
      </div>

      {/* Concentric-ring SVG: outer dashed amber advisory, inner red exclusion */}
      <div className="my-2 flex justify-center">
        <svg viewBox="0 0 160 96" className="h-24 w-full max-w-[200px]">
          <defs>
            <radialGradient id={`zone-grad-${zone.id}`} cx="50%" cy="92%" r="78%">
              <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#EF4444" stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* advisory zone */}
          <ellipse cx="80" cy="88" rx="74" ry="30" fill={`url(#zone-grad-${zone.id})`} />
          <ellipse
            cx="80"
            cy="88"
            rx="74"
            ry="30"
            fill="none"
            stroke="#F59E0B"
            strokeWidth="1"
            strokeDasharray="4 4"
            opacity="0.55"
          />
          {/* red exclusion zone */}
          <ellipse
            cx="80"
            cy="88"
            rx={redRx}
            ry={redRy}
            fill="none"
            stroke="#EF4444"
            strokeWidth="1.4"
            opacity="0.9"
          />
          <text x="6" y="92" fill="#F59E0B" fontSize="7" fontFamily="monospace">
            ADVISORY {zone.advisory_zone_m}m
          </text>
          <text x="112" y="92" fill="#EF4444" fontSize="7" fontFamily="monospace">
            RED {zone.red_zone_m}m
          </text>
        </svg>
      </div>

      <div className="mono mb-2 text-center text-[11px] text-slatey">
        <span className="text-critical">RED {zone.red_zone_m}m</span>
        {" / "}
        <span className="text-accent">ADVISORY {zone.advisory_zone_m}m</span>
      </div>

      <div className="flex items-center justify-center gap-1.5 text-[11px]">
        {zone.auto_intervention ? (
          <span className="flex items-center gap-1 text-aurora-teal">
            <ShieldAlert size={13} /> auto-intervention
          </span>
        ) : (
          <span className="flex items-center gap-1 text-slatey">
            <ShieldAlert size={13} /> advisory only
          </span>
        )}
      </div>
    </div>
  );
}
